import { LP_DECIMALS, VERSION_0 } from '@/constants';
import {
  useAddLiquidityStore,
  useBurnLiquidityStore,
  useCreatePoolStore,
  useStore,
  useSwapStore,
  useTokensStore,
} from '@/store';
import { TCurveType } from '@/types';
import {
  useCurrencyFormat,
  useNumberFormat,
} from '@/composables/useCurrencyFormat';
import {
  PoolCbOptions,
  showPoolDetailsDialog,
  TDialog,
} from '@/composables/useDialogService';
import { amount2NoENotation } from '@/composables/useInputServices';
import {
  FilteredMappedPools,
  FilterName,
  IPersistedPool,
  IPersistedPoolSolo,
  IPersistedPoolV1,
  ITablePool,
  ITablePoolV1,
  MappedPools,
} from '@/types/pools';
import stableSvg from '@/assets/curves/stable.svg';
import uncorrelatedSvg from '@/assets/curves/uncorrelated.svg';
import { d } from '@/utils';
import { computed, ref } from 'vue';
import { cloneDeep } from 'lodash';
import { providerForToken } from '@/utils/tokens';
import { Version } from './useContractVersion';
import { CORRECT_CHAIN_ID } from '@/constants/networks';

const tokensStore = useTokensStore();

/**
 * retrieves the short name of the curve from the full name
 * @param curve
 * @returns
 */
export const getCurveFromComplexCurve = computed(() => (curve: string) => {
  const [, , curveShortName] = curve.split('::');

  return !curveShortName
    ? undefined
    : (curveShortName as 'Uncorrelated' | 'Stable');
});

const isMovement = computed(() => {
  return CORRECT_CHAIN_ID === 126;
});

export const curve2Label = computed(() => (curve: string) => {
  return getCurveFromComplexCurve.value(curve) === 'Uncorrelated'
    ? 'Uncorrelated'
    : 'Stable';
});

export const lpToLabel = computed(() => (amountLp: number) => {
  return useNumberFormat(amountLp, { decimals: LP_DECIMALS, suffix: '' }).value;
});

export const share2label = computed(() => (share: number | undefined) => {
  if (!share) {
    return '--';
  }

  return share < 0.01 ? '< 0.01 %' : `${share.toFixed(2)} %`;
});

export const getCurveImg = computed(() => (curve: TCurveType) => {
  return getCurveFromComplexCurve.value(curve) === 'Uncorrelated'
    ? uncorrelatedSvg
    : stableSvg;
});

const store = useStore();
/**
 * Function of displaying pool details on mobile devices
 * @param event - @select-row event of prime Table
 * @param dialog - Instance of service
 * @param closeCb - Behavior of the component that triggered the dialog when closing
 * @returns
 */
export const showPoolDetails = computed(
  () =>
    (
      event: any,
      dialog: TDialog,
      closeCb: (options: PoolCbOptions) => void,
    ) => {
      if (!store.isMobile.value) return;

      showPoolDetailsDialog(dialog, { pool: event.data }, closeCb);
    },
);

export const tvl2Label = computed(() => (tvl: number | undefined) => {
  const amount = amount2NoENotation(tvl ? '' + tvl : '0', '');
  return tvl ? `$${amount}` : '--';
});

export const reserves2label = computed(
  () => (value: string | number, coin: string) => {
    return useCurrencyFormat(value, coin, {
      useSuffix: false,
    }).formatted.value;
  },
);

const swapStore = useSwapStore();
const addLiquidityStore = useAddLiquidityStore();
const burnLiquidity = useBurnLiquidityStore();
const createPoolStore = useCreatePoolStore();

/**
 * setting the value of tokens in storage
 * @param pool
 * @param type
 * @returns
 */
export function setTokensToStore(
  pool: ITablePool,
  type: 'swap' | 'add-liquidity' | 'burn-liquidity' | 'create-pool',
) {
  const store =
    type === 'swap'
      ? swapStore
      : type === 'add-liquidity'
      ? addLiquidityStore
      : type === 'burn-liquidity'
      ? burnLiquidity
      : type === 'create-pool'
      ? createPoolStore
      : undefined;

  if (!store) return;

  store.curve = pool.curve;
  store.fromCurrency.token = pool.coinX;
  store.toCurrency.token = pool.coinY;
  store.version = pool?.contract ? (pool.contract as Version) : VERSION_0;

  if (type === 'burn-liquidity') {
    burnLiquidity.shouldFindOptimalPool = false;
  }
}

export const apr2Label = (apr: number | string | undefined) => {
  const numberApr = Number(apr);

  if (apr === undefined || Number.isNaN(numberApr)) {
    return '—';
  }

  if (numberApr === 0) {
    return '< 0.01%';
  }

  return `${numberApr.toFixed(2)} %`;
};

export const getFees = computed(
  () => (apr: number, added: number, decimal: number) => {
    if (!apr || !added || !decimal) return 0;

    const denominator = d(10).pow(decimal);
    const aprPercentage = d(apr).div(100);
    const fee = d(added).div(denominator).mul(aprPercentage).toNumber();

    return Intl.NumberFormat('en', {
      notation: 'standard',
      minimumFractionDigits: 0,
      maximumFractionDigits: decimal,
    }).format(fee);
  },
);

export const getTokenAmounts = computed(
  () => (data?: ITablePool | ITablePoolV1) => {
    if (!data || !data.tokenEntities) return;

    const xSymbol = computed(() => data.tokenEntities?.fromTokenEntity?.symbol);
    const ySymbol = computed(() => data.tokenEntities?.toTokenEntity?.symbol);

    const xTokensAmountFormatted = Number(data?.addedX).toFixed(
      data.tokenEntities?.fromTokenEntity?.decimals,
    );

    const yTokensAmountFormatted = Number(data?.addedY).toFixed(
      data.tokenEntities?.toTokenEntity?.decimals,
    );

    return [
      xTokensAmountFormatted
        ? `${xTokensAmountFormatted} ${xSymbol.value}`
        : '--',
      yTokensAmountFormatted
        ? `${yTokensAmountFormatted} ${ySymbol.value}`
        : '--',
    ];
  },
);

type ItemPools = IPersistedPool | IPersistedPoolV1 | IPersistedPoolSolo;

export const formatAmountDecimals = (amount: number, decimals = 0) => {
  return useNumberFormat(amount, {
    decimals: decimals,
    suffix: '',
  }).value;
};

export const getUSDAmount = computed(() => (data: ItemPools) => {
  const totalUsd = data.totalUsd;
  return amount2NoENotation(totalUsd ? ' ' + totalUsd : '0', '$');
});

export const onMappedPools = computed(
  () => (pools: Array<ItemPools>) =>
    pools.reduce((acc: Array<ItemPools>, item: ItemPools) => {
      const fromTokenEntity = tokensStore.getToken(item.coinX);
      const toTokenEntity = tokensStore.getToken(item.coinY);
      if (!fromTokenEntity || !toTokenEntity) {
        return acc;
      }
      return [
        ...cloneDeep(acc),

        {
          ...cloneDeep(item),

          tokenEntities: {
            fromTokenEntity: tokensStore.getToken(item.coinX),
            toTokenEntity: tokensStore.getToken(item.coinY),
          },
        },
      ];
    }, []),
);

export const onMappedMyPools = computed(() => (pools: Array<ItemPools>) => {
  return onMappedPools.value(pools).filter((item) => {
    if (Number(item.addedX) > 0 || Number(item.addedY) > 0) {
      return true;
    }
    return false;
  });
});

export const filteredBySearchQuery = computed(
  () => (str: string, array: MappedPools) => {
    const searchString = str?.toLowerCase();

    return array.filter((item) => {
      // filter by symbol of the 1st token
      if (
        item?.tokenEntities?.fromTokenEntity?.symbol
          ?.toLowerCase()
          .indexOf(searchString) !== -1
      ) {
        return true;
      }
      // provider 1st token
      else if (
        providerForToken(item?.tokenEntities?.fromTokenEntity)
          ?.toLowerCase()
          .indexOf(searchString) !== -1
      ) {
        return true;
      }
      // filter by symbol of the 2nd token
      else if (
        item?.tokenEntities?.toTokenEntity?.symbol
          ?.toLowerCase()
          .indexOf(searchString) !== -1
      ) {
        return true;
      }
      // provider of the 2nd token
      else if (
        providerForToken(item?.tokenEntities?.toTokenEntity)
          ?.toLowerCase()
          .indexOf(searchString) !== -1
      ) {
        return true;
      } else if ('curve' in item === false) {
        return false;
      }
      // filter by uncorrelated curve
      else if ('uncorrelated'.indexOf(searchString) !== -1) {
        return curve2Label.value(item.curve) === 'Uncorrelated';
      }
      // filter by stable curve
      else if ('stable'.indexOf(searchString) !== -1) {
        return curve2Label.value(item.curve) === 'Stable';
      }

      return false;
    });
  },
);

const filters = isMovement.value
  ? ref<FilterName[]>(['ALL', 'TOP', 'APT', 'USDCe', 'USDTe', 'WETHe'])
  : ref<FilterName[]>(['ALL', 'TOP', 'APT', 'USDC', 'USDT', 'WETH']);

export const filtersMappedPools = computed(
  () =>
    (pools: MappedPools): Partial<FilteredMappedPools> => {
      const poolsFilteredFromToken = filters.value.reduce((obj, filterName) => {
        if (filterName === 'ALL' || filterName === 'TOP') {
          return obj;
        }
        const filteredPools = filteredBySearchQuery
          .value(filterName, pools)
          .filter(Boolean);
        const count = filteredPools.length;

        if (count) {
          const { tokenEntities } = filteredPools[0];
          const token = tokenEntities?.fromTokenEntity?.symbol === filterName;
          const logo = token
            ? tokenEntities?.fromTokenEntity?.logo
            : tokenEntities?.toTokenEntity?.logo;

          return {
            ...cloneDeep(obj),
            [filterName]: { filterName, pools: filteredPools, count, logo },
          };
        }
        return obj;
      }, {});

      return {
        ALL: {
          filterName: 'ALL',
          pools: [...cloneDeep(pools)],
          count: pools.length,
        },
        ...poolsFilteredFromToken,
      };
    },
);

export const getTop20Volume = computed(() => (array: ITablePool[]) => {
  return array
    .toSorted((a, b) => {
      return (b.volume24USD || 0) - (a.volume24USD || 0);
    })
    .slice(0, 20);
});

export const volume24USDToLabel = computed(
  () => (volume24USD: number | undefined) => {
    if (volume24USD === undefined || volume24USD < 1) return '< $1';

    const amount = Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
    }).format(volume24USD);

    return `$${amount}`;
  },
);

export const isNeedToShowVolume = computed(
  () => (volume24USD: number | undefined) => {
    if (!volume24USD) return undefined;

    return volume24USD > 0.009;
  },
);
